<template>
    <h2 class="text-lg font-bold uppercase tracking-wide text-gray-900">
        {{ $t("orderModifications.approval") }}
    </h2>
    <p class="text-gray-500 text-sm">
        {{ $t("orderModifications.approvalInfo") }}
    </p>

    <div class="mt-4 flex items-center space-x-3">
        <!-- SEND TO CUSTOMER -->
        <div v-if="showRelease">
            <p class="text-blue-700 font-bold">
                {{ $t("orderModifications.orderModificationNotSent") }}
            </p>
            <p class="text-blue-500 mb-3">
                {{ $t("orderModifications.orderModificationNotSentDesc") }}
            </p>
            <button
                @click="release"
                class="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition"
            >
                <PaperAirplaneIcon class="h-5 w-5 text-blue-500" />
                {{ $t("orderModifications.send") }}
            </button>
        </div>

        <!-- DECISION -->
        <div v-if="showApprove" class="w-full">
            <div v-if="pendingOffers.length > 0" class="mb-5 p-4 bg-orange-50 border-l-4 border-orange-400 text-orange-800 rounded shadow-sm">
                <div class="flex items-center">
                    <ExclamationCircleIcon class="h-5 w-5 mr-3 text-orange-500" />
                    <div class="flex-1">
                        <span class="font-bold block">{{ $t("orderModifications.pendingSignatures.title") }}</span>
                        <p class="text-sm">{{ $t("orderModifications.pendingSignatures.boxDescription") }}</p>
                    </div>
                    <button @click="showPendingModal = true" class="ml-4 px-3 py-1 bg-orange-100 text-orange-700 text-xs font-bold rounded hover:bg-orange-200 transition uppercase tracking-wider">
                        {{ $page.props.user.buyer_id ? $t("orderModifications.pendingSignatures.showBuyer") : $t("orderModifications.pendingSignatures.showAdvisor") }}
                    </button>
                </div>
            </div>
            <div>
                <p class="font-bold text-gray-900">
                    {{ $t("orderModifications.orderModificationDecision") }}
                </p>
                <p class="mb-3 text-gray-500 lg:pr-28">
                    {{ $t("orderModifications.orderModificationDecisionDesc") }}
                </p>
            </div>
            <div class="flex">
                <!-- APPROVE -->
                <button
                    @click="approve"
                    class="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-green-700 bg-green-100 rounded-lg hover:bg-green-200 transition"
                >
                    <CheckIcon class="h-5 w-5 text-green-500" />
                    {{ $t("orderModifications.approve") }}
                </button>
                <!-- DECLINE -->
                <button
                    v-if="showDecline && rejectModificationRoute"
                    @click="openModal(rejectModificationRoute)"
                    class="flex items-center ml-3 gap-2 px-4 py-2 text-sm font-semibold text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition"
                >
                    <XIcon class="h-5 w-5 text-red-500" />
                    {{ $t("orderModifications.decline") }}
                </button>
            </div>
        </div>
    </div>

    <!-- Pending Signatures Modal -->
    <jet-dialog-modal :show="showPendingModal" @close="showPendingModal = false" max-width="2xl">
        <template #title>
            {{ $t("orderModifications.pendingSignatures.modalTitle") }}
        </template>
        <template #content>
            <p class="text-sm text-gray-600 mb-4">
                {{ $page.props.user.buyer_id
                    ? $t("orderModifications.pendingSignatures.introBuyer")
                    : $t("orderModifications.pendingSignatures.introAdvisor") }}
            </p>
            <div class="overflow-x-auto border rounded">
                <table class="min-w-full divide-y text-sm">
                    <thead class="bg-gray-50">
                        <tr>
                            <th class="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">{{ $t("orderModifications.pendingSignatures.colOffer") }}</th>
                            <th class="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">{{ $t("orderModifications.pendingSignatures.colBuyer") }}</th>
                            <th class="px-4 py-2 text-right font-medium text-gray-500 uppercase tracking-wider">{{ $t("orderModifications.pendingSignatures.colAction") }}</th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y">
                        <template v-for="offer in pendingOffers" :key="offer.uuid">
                            <tr v-for="buyer in offer.signature_status.history.filter(h => h.is_sign !== 'Yes' && h.is_sign !== 'reject')" :key="offer.uuid + buyer.buyer_id">
                                <td class="px-4 py-2 whitespace-nowrap">{{ offer.title }}</td>
                                <td class="px-4 py-2 whitespace-nowrap">
                                    {{ buyer.buyer_name }}
                                    <span v-if="buyer.is_sign === 'reject'" class="ml-2 px-1 text-[10px] font-bold bg-red-100 text-red-700 rounded uppercase">{{ $t("orderModifications.pendingSignatures.rejected") }}</span>
                                </td>
                                <td class="px-4 py-2 whitespace-nowrap text-right">
                                    <button v-if="!$page.props.user.buyer_id && buyer.is_sign !== 'reject'" :disabled="resendingEmailId === buyer.buyer_id" @click="resendEmail(offer, buyer)" class="text-minplan-blue hover:text-minplan-darkblue font-medium text-xs border border-minplan-blue px-2 py-1 rounded disabled:opacity-50">
                                        {{ resendingEmailId === buyer.buyer_id ? $t("orderModifications.pendingSignatures.sending") : $t("orderModifications.pendingSignatures.resendEmail") }}
                                    </button>
                                    <button v-else-if="isSignableByMe(offer, buyer)" @click="openSignModal(offer, buyer)" class="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-bold text-xs border border-emerald-600 px-2 py-1 rounded">
                                        <PencilAltIcon class="h-3 w-3" />
                                        {{ buyer.buyer_name ? $t("orderModifications.pendingSignatures.signNowFor", { name: buyer.buyer_name }) : $t("orderModifications.pendingSignatures.signNow") }}
                                    </button>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </template>
        <template #footer>
            <div class="flex justify-between w-full">
                <p class="text-xs text-red-500 font-bold self-center">{{ $t("orderModifications.pendingSignatures.approvalBlocked") }}</p>
                <jet-secondary-button @click="showPendingModal = false">
                    {{ $t("actions.close") }}
                </jet-secondary-button>
            </div>
        </template>
    </jet-dialog-modal>

    <!-- Approve Confirmation Modal -->
    <jet-dialog-modal :show="showConfirmModal" @close="showConfirmModal = false" max-width="lg">
        <template #title>
            {{ $t("orderModifications.confirmApprove.title") }}
        </template>
        <template #content>
            <p class="font-bold text-gray-900 mb-2">
                {{ $t("orderModifications.confirmApprove.question") }}
            </p>
            <p class="text-sm text-gray-600">
                {{ $t("orderModifications.confirmApprove.description") }}
            </p>
        </template>
        <template #footer>
            <div class="flex justify-end gap-3 w-full">
                <jet-secondary-button @click="showConfirmModal = false">
                    {{ $t("actions.cancel") }}
                </jet-secondary-button>
                <button
                    @click="confirmApprove"
                    class="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-green-700 bg-green-100 rounded-lg hover:bg-green-200 transition"
                >
                    <CheckIcon class="h-5 w-5 text-green-500" />
                    {{ $t("orderModifications.confirmApprove.confirm") }}
                </button>
            </div>
        </template>
    </jet-dialog-modal>

    <!-- Sign Modal (Iframe) -->
    <jet-dialog-modal :show="!!signingUrl" @close="signingUrl = null" max-width="full">
        <template #title>
            <div class="flex justify-between items-center pr-8">
                <span>{{ $t("orderModifications.pendingSignatures.signDocument") }}</span>
                <button @click="checkBuyerCompletion" class="bg-emerald-600 text-white px-4 py-1 rounded text-sm font-bold shadow hover:bg-emerald-700 transition">
                    {{ $t("orderModifications.pendingSignatures.signingDone") }}
                </button>
            </div>
        </template>
        <template #content>
            <div class="w-full h-[85vh] border rounded overflow-hidden bg-gray-50 relative">
                <iframe v-if="signingUrl" :src="signingUrl" class="w-full h-full" frameborder="0"></iframe>
            </div>
        </template>
        <template #footer>
            <div class="flex justify-between w-full">
                <p class="text-xs text-gray-500 italic pt-2">{{ $t("orderModifications.pendingSignatures.signHint") }}</p>
                <jet-secondary-button @click="signingUrl = null">
                    {{ $t("actions.close") }}
                </jet-secondary-button>
            </div>
        </template>
    </jet-dialog-modal>
</template>

<script>
import { CheckIcon, PaperAirplaneIcon, XIcon, ExclamationCircleIcon, PencilAltIcon } from "@heroicons/vue/outline";
import JetDialogModal from "@/Jetstream/DialogModal";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import axios from "axios";

export default {
    components: { CheckIcon, XIcon, PaperAirplaneIcon, JetDialogModal, JetSecondaryButton, ExclamationCircleIcon, PencilAltIcon },
    props: [
        "showDecline",
        "showRelease",
        "showApprove",
        "rejectModificationRoute",
        "patchModificationRoute",
        "offers",
    ],
    data() {
        return {
            showPendingModal: false,
            showConfirmModal: false,
            resendingEmailId: null,
            signingUrl: null,
            signingOfferUuid: null,
            signingBuyerId: null,
            signingOpenSignId: null,
            pollingInterval: null,
        };
    },
    watch: {
        signingUrl(newVal) {
            if (newVal) {
                this.startPolling();
            } else {
                this.stopPolling();
            }
        }
    },
    beforeUnmount() {
        this.stopPolling();
    },
    computed: {
        pendingOffers() {
            if (!this.offers) return [];
            const items = Array.isArray(this.offers) ? this.offers : Object.values(this.offers);
            return items.filter(offer => {
                return offer.signature_status && offer.signature_status.completed < offer.signature_status.total;
            });
        }
    },
    methods: {
        release() {
            this.update({
                updateState: {
                    transition: "release",
                },
            });
        },
        approve() {
            if (this.pendingOffers.length > 0) {
                this.showPendingModal = true;
                return;
            }
            this.showConfirmModal = true;
        },
        confirmApprove() {
            this.showConfirmModal = false;
            this.update({
                updateState: {
                    transition: "approve",
                },
            });
        },
        update(payload) {
            this.$inertia.patch(this.patchModificationRoute, payload, {
                onSuccess: () =>
                    this.$notify({
                        group: "success",
                        title: "actions.success",
                        text: "orderModifications.updated",
                    }),
            });
        },
        openModal(link) {
            this.$inertia.visitInModal(link);
        },
        // Der Server sagt, welche Kaeufer-Zeilen dieses Login unterschreiben
        // darf. Frueher verglich das Frontend `buyer.buyer_id` gegen die EINE
        // `user.buyer_id` aus den Shared Props — ein Login mit mehreren
        // Kaeufer-Zeilen (je Wohnung eine) traf damit hoechstens auf einer
        // Wohnung zu, und auf allen uebrigen blieb der Knopf lautlos aus.
        //
        // `buyer_id` kommt aus dem OpenSign-JSON als String, die Liste vom
        // Server als Zahl. Ohne `Number()` faende `includes` nie etwas — und
        // zwar genauso lautlos.
        isSignableByMe(offer, buyer) {
            const ids = offer.signature_status?.signable_buyer_ids ?? [];

            return ids.includes(Number(buyer.buyer_id));
        },
        openSignModal(offer, buyer) {
            this.showPendingModal = false;
            this.signingUrl = buyer.url;
            this.signingOfferUuid = offer.uuid;
            this.signingBuyerId = Number(buyer.buyer_id);
            this.signingOpenSignId = offer.digital_sign_json?.docId;
        },
        async checkBuyerCompletion(silent = false) {
            if (!this.signingOfferUuid) return;
            try {
                const res = await axios.post('/documents/digital-sign/complete-buyer', {
                    docId: this.signingOpenSignId,
                    documentUuid: this.signingOfferUuid,
                    buyerId: this.signingBuyerId,
                });
                if (res.data?.ok) {
                    this.stopPolling();
                    this.signingUrl = null;
                    this.$inertia.reload();
                    this.$notify({ group: "success", title: "actions.success", text: "orderModifications.pendingSignatures.signatureProcessed" });
                }
            } catch (e) {
                if (!silent) console.error(e);
            }
        },
        startPolling() {
            this.stopPolling();
            const interval = (parseInt(this.$page.props.check_sign_complete_or_not_in_second) || 2) * 1000;
            this.pollingInterval = setInterval(() => {
                this.checkBuyerCompletion(true);
            }, interval);
        },

        stopPolling() {
            if (this.pollingInterval) {
                clearInterval(this.pollingInterval);
                this.pollingInterval = null;
            }
        },
        async resendEmail(offer, buyer) {
            this.resendingEmailId = buyer.buyer_id;
            try {
                const res = await axios.post('/documents/digital-sign/resend-email', {
                    documentUuid: offer.uuid,
                    buyerId: buyer.buyer_id
                });
                if (res.data?.ok) {
                    this.$notify({ group: "success", title: "actions.success", text: "orderModifications.pendingSignatures.reminderSent" });
                } else {
                    this.$notify({ group: "error", title: "actions.error", text: res.data?.error || "orderModifications.pendingSignatures.reminderFailed" });
                }
            } catch (e) {
                console.error(e);
                this.$notify({ group: "error", title: "actions.error", text: "orderModifications.pendingSignatures.genericError" });
            } finally {
                this.resendingEmailId = null;
            }
        },
    },
};
</script>
<style scoped></style>
